import { useContext, useEffect, useState } from "react";
import cx from "classnames";
import { Link, NavLink, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactComponent as Logo } from "../../images/ttNavLogo.svg";
import {
  faQuestionCircle,
  faGraduationCap,
  faThLarge,
} from "@fortawesome/pro-light-svg-icons";
import AppRoutes from "../../routes/AppRoutes";
import UserContext from "../../state/UserContext";
import { useTranslation } from "react-i18next";
import AppContext from "../../state/AppContext";
import {
  imageHelper,
  moduleAccessHelper,
  userDetailsHelper,
} from "../../helpers";
import inAppSupportHelper from "../../helpers/inAppSupportHelper";
import AnalyticsFiltersWalkthrough from "../user-walkthrough/AnalyticsFiltersWalkthrough";
import ManagerDashWalkthrough from "../user-walkthrough/ManagerDashWalkthrough";
import MyDashWalkthrough from "../user-walkthrough/MyDashWalkthrough";
import UserwalkthroughType from "../../types/generic/UserWalkthroughType";
import LeftSubNavGroup from "./LeftSubNavGroup";
import { AnalyticsSubNavItems } from "../../routes/analytics";
import ttModulesList from "../../config/modules/ttModulesList";
import AdminSubNavItems from "../../routes/admin/AdminSubNavItems";
import { adminHelper } from "../../helpers/adminHelper";
import YourJourneySubNavItems from "../../routes/your-journey/YourJourneySubNavItems";
import YourPeopleSubNavItems from "../../routes/your-people/YourPeopleSubNavItems";
import { PoweredByKorero } from "../layout/PoweredByKorero";
import permissionHelper from "../../helpers/permissionHelper";
import NavIcon from "./NavIcon";

function getMatchingSubNavGroup(pathname: string): string | null {
  if (pathname.startsWith(AppRoutes.yourJourney.root)) {
    return "your-dashboard-subnav";
  }
  if (pathname.startsWith(AppRoutes.yourPeople.root)) {
    return "your-people-subnav";
  }
  if (pathname.startsWith(AppRoutes.admin.root)) {
    return "admin-subnav";
  }
  if (pathname.startsWith(AppRoutes.analytics.root)) {
    return "analytics-subnav";
  }
  return null;
}

function LeftNav() {
  const location = useLocation();
  const appContext = useContext(AppContext);
  const userContext = useContext(UserContext);
  const [
    activeDashboardTypeForWalkthrough,
    setActiveDashboardTypeForWalkthrough,
  ] = useState<UserwalkthroughType | null>(
    appContext.activeDashboardTypeForWalkthrough,
  );
  const { t } = useTranslation();
  const [launchWalkthrough, setLaunchWalkthrough] = useState<boolean>(false);
  const [expandedSubNavGroups, setExpandedSubNavGroups] = useState<Set<string>>(
    () => {
      const matching = getMatchingSubNavGroup(location.pathname);
      return matching ? new Set([matching]) : new Set();
    },
  );

  useEffect(() => {
    const matchingSubNav = getMatchingSubNavGroup(location.pathname);
    setExpandedSubNavGroups(matchingSubNav ? new Set([matchingSubNav]) : new Set());
  }, [location.pathname]);

  useEffect(() => {
    setActiveDashboardTypeForWalkthrough(
      appContext.activeDashboardTypeForWalkthrough,
    );
    setLaunchWalkthrough(false);
  }, [appContext.activeDashboardTypeForWalkthrough]);

  const isInExitMode = userContext.user.myDashboardMode === "EXIT";

  const shouldShowAnalyticsNavItem =
    permissionHelper.shouldShowAnalyticsNavigation(userContext);

  const shouldShowCompanyDashboardNavItem = moduleAccessHelper.userHasModule(
    userContext.user,
    ttModulesList.CompanyDash,
  );
  const shouldShowClassicAnalyticsNavItem =
    shouldShowAnalyticsNavItem &&
    moduleAccessHelper.userHasModule(userContext.user, ttModulesList.Reviews);

  const canUserUploadScores =
    shouldShowAnalyticsNavItem &&
    permissionHelper.userHasEitherPermission(
      userContext,
      "CanUploadExternalScores",
      "UPLOAD-EXTERNAL-SCORES",
    );

  const canAccessExports =
    shouldShowAnalyticsNavItem && userContext.user.isAdmin;

  const canManageSharedAnalyticsFilters =
    permissionHelper.userHasEitherPermission(
      userContext,
      "ManageSharedAnalyticsFilters",
      "MANAGE-SHARED-ANALYTICS-FILTERS",
    );

  const headerLogo = userContext.user.client.logoFilename ? (
    <img
      src={imageHelper.getImagePath(userContext.user.client.logoFilename, 200)}
      alt={userContext.user.client.name}
      className="px-2 m-auto w-48"
    />
  ) : (
    <Logo className="px-2 m-auto" width={"12rem"} aria-label={userContext.user.client.productName} />
  );

  const handleLaunchWalkthrough = () => {
    setLaunchWalkthrough(true);
  };

  const handleCloseWalkthrough = () => {
    setLaunchWalkthrough(false);
  };

  const handleSubNavExpandToggle = (navItemId: string) => {
    const routeMatchedSubNav = getMatchingSubNavGroup(location.pathname);
    setExpandedSubNavGroups((current) => {
      const next = new Set(current);
      if (next.has(navItemId)) {
        if (routeMatchedSubNav === navItemId) {
          return current;
        }
        next.delete(navItemId);
      } else {
        next.add(navItemId);
      }
      return next;
    });
  };

  const hasAiAnalytics = moduleAccessHelper.userHasModule(
    userContext.user,
    "AI Analytics"
  );

  const analyticsSubNavItems = AnalyticsSubNavItems(
    userContext.user.analyticsNavItems,
    shouldShowClassicAnalyticsNavItem,
    canUserUploadScores,
    canAccessExports,
    "LEFT-NAV",
    hasAiAnalytics
  );

  const adminSubNavItems = AdminSubNavItems(userContext, "LEFT-NAV");
  const hasVisibleAdminDashboard =
    adminHelper.hasVisibleAdminDashboardCards(userContext);
  const shouldShowAdminNavGroup =
    !isInExitMode &&
    (adminSubNavItems.length > 0 || hasVisibleAdminDashboard);
  const yourDashboardSubNavItems = YourJourneySubNavItems(userContext.user);
  const yourPeopleSubNavItems = YourPeopleSubNavItems(userContext.user);
  const managerBadgeNumber = userDetailsHelper.getManagerUpdateCount(
    userContext.user,
  );

  const myDashboardBadgeNumber = userDetailsHelper.getOwnUpdateCount(
    userContext.user,
  );

  return (
    <div id="left-navigation-container">
      <Link
        className="block px-4 py-2 my-4 text-center"
        to={AppRoutes.yourJourney.root}
      >
        {headerLogo}
      </Link>

      <nav id="left-navigation" className="flex flex-col">
        <hr className="initial-hr" />
        <LeftSubNavGroup
          subNavId="your-dashboard-subnav"
          isExpanded={expandedSubNavGroups.has("your-dashboard-subnav")}
          items={yourDashboardSubNavItems}
          baseRoute={AppRoutes.yourJourney.root}
          baseLinkText={t("Nav.LargeScreen.MyDashboard.Root")}
          highlightRootLevel={myDashboardBadgeNumber > 0}
          onToggleExpand={handleSubNavExpandToggle}
        />

        {(userContext.user.isManager || userContext.user.isJourneyManager) && (
          <LeftSubNavGroup
            subNavId="your-people-subnav"
            isExpanded={expandedSubNavGroups.has("your-people-subnav")}
            items={yourPeopleSubNavItems}
            baseRoute={AppRoutes.yourPeople.root}
            baseLinkText={t("Nav.LargeScreen.ManagerDashboard.Root")}
            highlightRootLevel={managerBadgeNumber > 0}
            onToggleExpand={handleSubNavExpandToggle}
          />
        )}

        {shouldShowAdminNavGroup && (
          <LeftSubNavGroup
            subNavId="admin-subnav"
            isExpanded={expandedSubNavGroups.has("admin-subnav")}
            items={adminSubNavItems}
            baseRoute={AppRoutes.admin.root}
            baseLinkText={t("Nav.LargeScreen.Admin.Root")}
            onToggleExpand={handleSubNavExpandToggle}
          />
        )}

        {shouldShowAnalyticsNavItem && (
          <LeftSubNavGroup
            subNavId="analytics-subnav"
            isExpanded={expandedSubNavGroups.has("analytics-subnav")}
            items={analyticsSubNavItems}
            baseRoute={AppRoutes.analytics.root}
            baseLinkText={t("Nav.LargeScreen.Analytics.Root")}
            onToggleExpand={handleSubNavExpandToggle}
          />
        )}

        <hr />

        {shouldShowCompanyDashboardNavItem && (
          <div className="navigation-item">
            <NavLink
              className={({ isActive }) =>
                cx(
                  "navigation-link",
                  isActive ? "navigation-link-active" : "navigation-link-inactive"
                )
              }
              to={AppRoutes.companyDashboard}
            >
              {({ isActive }) => (
                <>
                  <NavIcon icon={faThLarge} isSelected={isActive} />
                  <span className="navigation-item-text">
                    {t("Nav.LargeScreen.CompanyDashboard")}
                  </span>
                </>
              )}
            </NavLink>
          </div>
        )}

        <div className="navigation-item">
          {inAppSupportHelper.shouldShowForUser(userContext.user) ? (
            <a
              className="navigation-link navigation-link-inactive"
              href={AppRoutes.help}
              onClick={(e) => {
                e.preventDefault();
                inAppSupportHelper.toggle();
              }}
              aria-label={t("Nav.LargeScreen.Help")}
            >
              <NavIcon icon={faQuestionCircle} isSelected={false} />
              <span className="navigation-item-text">
                {t("Nav.LargeScreen.Help")}
              </span>
            </a>
          ) : (
            <a
              className="navigation-link navigation-link-inactive"
              href={AppRoutes.help}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t(
                "Nav.LargeScreen.HelpOpensNewTab",
                "Help (opens in a new tab)"
              )}
            >
              <NavIcon icon={faQuestionCircle} isSelected={false} />
              <span className="navigation-item-text">
                {t("Nav.LargeScreen.Help")}
              </span>
            </a>
          )}
        </div>

        {(activeDashboardTypeForWalkthrough === "MY-DASHBOARD" ||
          activeDashboardTypeForWalkthrough === "MANAGER-DASHBOARD" ||
          activeDashboardTypeForWalkthrough === "ANALYTICS-FILTERS") && (
          <button
            id="walkthrough-button"
            type="button"
            onClick={handleLaunchWalkthrough}
            aria-label={t(
              "Nav.LargeScreen.Walkthrough",
              "Introduction"
            )}
          >
            <span className="mx-2">
              {t("Nav.LargeScreen.Walkthrough", "Introduction")}
            </span>
            <FontAwesomeIcon
              icon={faGraduationCap}
              size="lg"
              className="fa-fw"
            />
          </button>
        )}
      </nav>
      {launchWalkthrough &&
        appContext.activeDashboardTypeForWalkthrough ===
          "MANAGER-DASHBOARD" && (
          <ManagerDashWalkthrough onFinished={handleCloseWalkthrough} />
        )}
      {launchWalkthrough &&
        appContext.activeDashboardTypeForWalkthrough === "MY-DASHBOARD" && (
          <MyDashWalkthrough
            hasClientGotAdvancedTaskTypes={
              userContext.user.client.taskTypes.length > 0
            }
            onFinished={handleCloseWalkthrough}
          />
        )}
      {launchWalkthrough &&
        appContext.activeDashboardTypeForWalkthrough ===
          "ANALYTICS-FILTERS" && (
          <AnalyticsFiltersWalkthrough
            canManageShared={canManageSharedAnalyticsFilters}
            onFinished={handleCloseWalkthrough}
          />
        )}

      <PoweredByKorero />
    </div>
  );
}

export default LeftNav;
